import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, IndianRupee, PackageX, Package, Boxes, Layers, Download, Upload, ClipboardList, Ruler, LayoutGrid, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { HandoffModal } from '@/components/ui/HandoffModal';
import { useAuth } from '@/auth/useAuth';
import {
  listWarehouseStock,
  listStockCollections,
  listStockCategories,
  NO_COLLECTION,
  fetchWarehouseShortages,
  warehouseStockExportPath,
  importWarehouseStock,
  type WarehouseStockRow,
} from '@/api/warehouseStock';
import { downloadExcel } from '@/lib/downloadExcel';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { createMrn } from '@/api/mrn';
import { listLocations } from '@/api/locations';
import { formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { AdjustStockModal } from './AdjustStockModal';
import { SetReorderModal } from './SetReorderModal';
import { ReservationsModal } from './ReservationsModal';

const STATUS_TONE: Record<WarehouseStockRow['stockStatus'], 'success' | 'warning' | 'danger' | 'muted'> = {
  'In Stock': 'success',
  'Low Stock': 'warning',
  'Out of Stock': 'danger',
  'Not Stocked Yet': 'muted',
};

export function WarehouseStockPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState<'all' | 'low' | 'zero' | 'ok' | 'unstocked'>('all');
  const [collection, setCollection] = useState('');
  const collectionsQ = useQuery({ queryKey: ['warehouse-stock-collections'], queryFn: listStockCollections, staleTime: 5 * 60_000 });
  const collectionOptions = useMemo(() => [
    { value: '', label: 'All collections' },
    ...(collectionsQ.data?.collections ?? []).map((c) => ({ value: c.name, label: c.name, sublabel: `${c.items} item${c.items === 1 ? '' : 's'}` })),
    ...(collectionsQ.data?.withoutCollection ? [{ value: NO_COLLECTION, label: 'No collection', sublabel: `${collectionsQ.data.withoutCollection} items` }] : []),
  ], [collectionsQ.data]);
  const [category, setCategory] = useState('');
  const categoriesQ = useQuery({ queryKey: ['warehouse-stock-categories'], queryFn: listStockCategories, staleTime: 5 * 60_000 });
  const [locationId, setLocationId] = useState<number | ''>('');
  const locationsQ = useQuery({ queryKey: ['locations-flat'], queryFn: () => listLocations({}), staleTime: 60_000 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const onSearch = useCallback((v: string) => { setSearch(v); setPage(1); }, []);
  const [adjustRow, setAdjustRow] = useState<WarehouseStockRow | null>(null);
  const [reorderRow, setReorderRow] = useState<WarehouseStockRow | null>(null);
  const [reservedRow, setReservedRow] = useState<WarehouseStockRow | null>(null);
  const [raisingAll, setRaisingAll] = useState(false);
  const [mrnHandoffOpen, setMrnHandoffOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { hasPerm } = useAuth();

  const filter = useMemo(
    () => ({ search: search || undefined, bucket, collection: collection || undefined, category: category || undefined, locationId: locationId || undefined, page, pageSize }),
    [search, bucket, collection, category, locationId, page, pageSize],
  );

  const stockQuery = useQuery({
    queryKey: ['warehouse-stock', filter],
    queryFn: () => listWarehouseStock(filter),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
  const rows = stockQuery.data?.data ?? [];
  const total = stockQuery.data?.total ?? 0;
  // KPIs follow the filters (search / item, collection, category, location).
  const kpis = stockQuery.data?.kpis;
  // Floor-area KPIs only make sense for carpets, doormats and runners.
  const area = /carpet|door\s*mat|runner/i.test(category) ? stockQuery.data?.area : undefined;
  const fmtArea = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const areaSub = area
    ? `${area.pcs.toLocaleString('en-IN')} pcs${area.unsizedItems ? ` · ${area.unsizedItems} item${area.unsizedItems === 1 ? '' : 's'} without size` : ''}`
    : undefined;

  // Shortage items (all pages) for the bulk-MRN action — fetched separately so
  // pagination of the table doesn't shrink the set.
  const shortagesQuery = useQuery({
    queryKey: ['warehouse-shortages'],
    queryFn: fetchWarehouseShortages,
    refetchInterval: 60_000,
  });
  const shortageRows = shortagesQuery.data ?? [];

  const raiseMrnMutation = useMutation({
    mutationFn: (items: WarehouseStockRow[]) =>
      createMrn({
        mrnDate: new Date().toISOString().slice(0, 10),
        purpose: 'Stock Replenishment',
        department: 'Warehouse',
        raisedBy: 'Warehouse',
        priority: 'High',
        approvalAuthority: 'HOD',
        items: items.map((r) => ({
          itemId: r.itemId,
          itemName: r.itemName,
          qty: r.reorderLevel && r.reorderLevel > r.currentQty ? r.reorderLevel - r.currentQty : 1,
          unit: r.unit,
          remarks: `Auto-raised from Warehouse Stock. Current: ${r.currentQty}, Reorder: ${r.reorderLevel ?? '—'}`,
        })),
      }),
    onSuccess: (mrn) => {
      qc.invalidateQueries({ queryKey: ['mrn'] });
      toast.pushStep(`Auto-raised MRN ${mrn.mrnNo}`, {
        body: 'Reorder MRN from low-stock items. Next step — submit for HOD approval.',
        href: '/purchase/mrn',
        nextLabel: 'Open MRN list',
        tag: `mrn-auto-${mrn.id}`,
      });
      navigate('/purchase/mrn');
    },
    onError: (e) => toast.push(apiError(e, 'Failed to create MRN'), 'error'),
  });

  async function handleRaiseAll() {
    if (shortageRows.length === 0) return;
    // Users without MRN permission can't create one directly — forward the
    // request to whoever owns the MRN module instead.
    if (!hasPerm('mrn')) {
      setMrnHandoffOpen(true);
      return;
    }
    if (!await confirmDlg(`Raise 1 MRN for all ${shortageRows.length} shortage/low-stock items?`)) return;
    setRaisingAll(true);
    try {
      await raiseMrnMutation.mutateAsync(shortageRows);
    } finally {
      setRaisingAll(false);
    }
  }

  return (
    <div>
      <SectionHeader
        title="Warehouse Stock"      />

      <div className={`mb-2 grid grid-cols-2 gap-1.5 sm:mb-2 sm:gap-3 ${area ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-6'}`}>
        <KpiCard label="Total Items" value={kpis?.totalItems ?? '—'} tone="primary" icon={Package} />
        <KpiCard label="Total Pcs" value={kpis ? kpis.totalPcs.toLocaleString('en-IN') : '—'} tone="indigo" icon={Layers} />
        <KpiCard label="Low Stock" value={kpis?.lowStock ?? '—'} tone="warning" icon={AlertTriangle} />
        <KpiCard label="Out of Stock" value={kpis?.outOfStock ?? '—'} tone="danger" icon={PackageX} />
        <KpiCard label="Not Stocked Yet" value={kpis?.notStocked ?? '—'} tone="muted" icon={Boxes} />
        <KpiCard
          label="Total Stock Value"
          value={kpis ? formatINR(kpis.totalValue) : '—'}
          tone="success"
          icon={IndianRupee}
        />
        {area && (
          <>
            <KpiCard label={`${category} area · Sq. Ft.`} value={fmtArea(area.sqft)} sub={areaSub} tone="teal" icon={Ruler} />
            <KpiCard label={`${category} area · Sq. Mtr.`} value={fmtArea(area.sqm)} sub={areaSub} tone="cyan" icon={LayoutGrid} />
          </>
        )}
      </div>

      <FilterBar>
        {/* No label — the placeholder and icon say what it is. */}
        <div className="col-span-2 min-w-0 sm:col-auto sm:w-[230px]">
          <SearchBox onSearch={onSearch} placeholder="Item, SKU, size or colour" label="Search warehouse stock" busy={stockQuery.isFetching} />
        </div>
        <FilterField label="Collection" className="sm:w-[200px]">
          <SearchableSelect
            options={collectionOptions}
            value={collection}
            onChange={(v) => { setCollection(v === '' ? '' : String(v)); setPage(1); }}
            placeholder="All collections"
            dense
          />
        </FilterField>
        <FilterField label="Category" className="sm:w-[180px]">
          <Select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {(categoriesQ.data ?? []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Location">
          <Select
            value={locationId === '' ? '' : String(locationId)}
            onChange={(e) => { setLocationId(e.target.value === '' ? '' : Number(e.target.value)); setPage(1); }}
          >
            <option value="">All locations</option>
            {(locationsQ.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Bucket">
          <Select
            value={bucket}
            onChange={(e) => {
              setBucket(e.target.value as 'all' | 'low' | 'zero' | 'ok' | 'unstocked');
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="ok">In Stock</option>
            <option value="low">Low Stock</option>
            <option value="zero">Out of Stock</option>
            <option value="unstocked">Not Stocked Yet</option>
          </Select>
        </FilterField>
        {/* Export / Import / Generate-MRN grouped together, right-pinned on
            desktop and a full-width wrapping row on mobile. */}
        <div className="col-span-3 flex flex-wrap items-end gap-2 sm:col-span-1 sm:ml-auto sm:self-end">
          <button
            type="button"
            onClick={() =>
              downloadExcel(warehouseStockExportPath({ search, bucket, collection, category, locationId }), 'GRHAMOY_WarehouseStock.xlsx')
            }
            title="Export the whole stock (all rows) to Excel"
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button type="button" onClick={() => setImportOpen(true)} title="Import stock from Excel" className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs">
            <Upload size={12} /> Import
          </button>
          {shortageRows.length > 0 && (
            <button
              type="button"
              onClick={handleRaiseAll}
              title={`Raise one MRN for all ${shortageRows.length} low / out-of-stock items`}
              disabled={raisingAll || raiseMrnMutation.isPending}
              className="inline-flex h-8 items-center justify-center gap-1 rounded-lg bg-accent px-3 text-xs font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60"
            >
              {raisingAll ? 'Creating MRN…' : <><ClipboardList size={13} className="mr-1 inline-block align-[-2px]" /> Generate MRN ({shortageRows.length})</>}
            </button>
          )}
        </div>
      </FilterBar>

      <Table<WarehouseStockRow>
        storageKey="warehouse-stock"
        title="Stock On Hand"
        count={`Showing ${rows.length} of ${total}`}
        rows={rows}
        rowKey={(r) => r.itemId}
        loading={stockQuery.isFetching && rows.length === 0}
        columns={[
          {
            key: 'img',
            header: '',
            width: '44px',
            align: 'center',
            alwaysVisible: true,
            cell: (r) => <ItemThumb src={r.imageUrl} size={28} />,
          },
          {
            key: 'barcode',
            header: 'SKU Barcode',
            cell: (r) =>
              r.barcode ? (
                <span className="font-extrabold text-accent" title={`Item code: ${r.itemCode}`}>{r.barcode}</span>
              ) : (
                <span className="text-[var(--text-muted)]" title={`Item code: ${r.itemCode}`}>—</span>
              ),
          },
          { key: 'name', header: 'Item Name', cell: (r) => <span className="font-bold">{r.itemName}</span> },
          { key: 'category', header: 'Category', cell: (r) => r.category ?? '—' },
          { key: 'unit', header: 'Unit', cell: (r) => r.unit },
          {
            key: 'opening',
            header: 'Opening',
            align: 'right',
            cell: (r) => r.openingQty,
          },
          {
            key: 'recv',
            header: <span title="Everything received: GRN purchases + audit / manual corrections, returns and transfers in">Overall Received</span>,
            align: 'right',
            cell: (r) => (
              <span className="text-success" title={`GRN ${r.grnInQty} · Adjusted ${r.adjustedInQty}`}>
                +{r.grnInQty + r.adjustedInQty}
              </span>
            ),
          },
          {
            key: 'iss',
            header: <span title="Everything issued: dispatches (outward GRNs, sales, transfers, department issues) + audit / manual write-offs">Overall Issued</span>,
            align: 'right',
            cell: (r) => (
              <span className="text-danger" title={`Dispatched ${r.dispatchedQty} · Adjusted / written off ${r.adjustedOutQty}`}>
                -{r.dispatchedQty + r.adjustedOutQty}
              </span>
            ),
          },
          {
            key: 'current',
            header: 'Current',
            align: 'right',
            cell: (r) => <strong>{r.currentQty}</strong>,
          },
          ...(locationId !== ''
            ? [{
                key: 'atLocation',
                header: 'At Location',
                align: 'right' as const,
                cell: (r: WarehouseStockRow) => <strong className="text-accent">{r.locationQty ?? 0}</strong>,
              }]
            : []),
          {
            key: 'reserved',
            header: 'Reserved',
            align: 'right',
            cell: (r) =>
              r.reservedQty > 0 ? (
                <button
                  type="button"
                  onClick={() => setReservedRow(r)}
                  // Fills the whole cell (over its 8px side padding and 36px row height) so anywhere in it opens the orders.
                  className="font-semibold text-warning underline decoration-dotted underline-offset-2 transition-colors hover:bg-warning/10 hover:text-accent sm:-mx-2 sm:flex sm:h-9 sm:w-[calc(100%+1rem)] sm:items-center sm:justify-center sm:px-2"
                  title="See which orders hold this stock"
                >
                  {r.reservedQty}
                </button>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              ),
          },
          {
            key: 'available',
            header: 'Available',
            align: 'right',
            cell: (r) => <strong className="text-info">{r.availableQty}</strong>,
          },
          {
            key: 'reorder',
            header: 'Reorder',
            align: 'right',
            cell: (r) => (
              <button
                onClick={() => setReorderRow(r)}
                className="rounded-lg border border-[var(--border-color)] bg-white/[0.04] px-2 py-0.5 text-xs font-bold text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
              >
                {r.reorderLevel || '—'}
              </button>
            ),
          },
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            cell: (r) => formatINR(r.stockValue),
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <Badge tone={STATUS_TONE[r.stockStatus]}>{r.stockStatus}</Badge>,
          },
          {
            key: 'actions',
            header: '',
            width: '220px',
            cell: (r) => (
              <div className="flex gap-1">
                <Button variant="primary" size="sm" onClick={() => setAdjustRow(r)}>
                  Adjust
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  disabled={raiseMrnMutation.isPending}
                  onClick={() => {
                    if (!hasPerm('mrn')) {
                      setMrnHandoffOpen(true);
                      return;
                    }
                    raiseMrnMutation.mutate([r]);
                  }}
                >
                  MRN
                </Button>
                <Link
                  to={`/inventory/wh-ledger?itemId=${r.itemId}`}
                  className="inline-flex items-center rounded-lg border border-[var(--border-color)] bg-white/[0.04] px-2.5 py-1.5 text-xs font-bold text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
                >
                  Ledger
                </Link>
              </div>
            ),
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <AdjustStockModal
        open={adjustRow !== null}
        row={adjustRow}
        onClose={() => setAdjustRow(null)}
      />
      <ReservationsModal row={reservedRow} onClose={() => setReservedRow(null)} />
      <SetReorderModal
        open={reorderRow !== null}
        row={reorderRow}
        onClose={() => setReorderRow(null)}
      />
      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Warehouse Stock"
        expectedColumns={['Item Code', 'SKU Barcode', 'Current']}
        onImport={importWarehouseStock}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
          qc.invalidateQueries({ queryKey: ['warehouse-stock-kpis'] });
        }}
      />

      {mrnHandoffOpen && (
        <HandoffModal
          open
          onClose={() => setMrnHandoffOpen(false)}
          action="raise an MRN for these low-stock items"
          payload={{
            moduleId: 'mrn',
            subject: `MRN needed — ${shortageRows.length} low-stock item${shortageRows.length === 1 ? '' : 's'}`,
            body: `Please raise an MRN for the current low-stock / out-of-stock items in warehouse. Top items: ${shortageRows.slice(0, 3).map((r) => r.itemName).join(', ')}${shortageRows.length > 3 ? `, +${shortageRows.length - 3} more` : ''}.`,
            entityType: 'wh-stock',
            actionHref: '/inventory/wh-stock',
          }}
        />
      )}
    </div>
  );
}

const KPI_ACCENT: Record<'success' | 'warning' | 'danger' | 'primary' | 'indigo' | 'muted' | 'teal' | 'cyan', { color: string; bg: string }> = {
  success: { color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  danger:  { color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  primary: { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  indigo:  { color: '#6366f1', bg: 'rgba(99,102,241,0.10)' },
  muted:   { color: '#64748b', bg: 'rgba(100,116,139,0.10)' },
  teal:    { color: '#0d9488', bg: 'rgba(13,148,136,0.10)' },
  cyan:    { color: '#0891b2', bg: 'rgba(8,145,178,0.10)' },
};

function KpiCard({
  label,
  value,
  sub,
  tone,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  /** Small line under the value. */
  sub?: string;
  tone: keyof typeof KPI_ACCENT;
  icon: LucideIcon;
}) {
  const { color } = KPI_ACCENT[tone];
  return (
    <div
      className="relative overflow-hidden rounded-lg p-2 text-white shadow-sm transition-transform hover:-translate-y-0.5 sm:rounded-xl sm:p-4"
      style={{ background: color, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-3 -right-3 h-16 w-16 sm:h-20 sm:w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="relative">
        <div className="text-[9px] font-extrabold uppercase tracking-widest text-white/90 sm:text-[10px]">{label}</div>
        <div className="mt-0.5 text-lg font-extrabold sm:mt-2 sm:text-2xl">{value}</div>
        {sub && <div className="mt-0.5 text-[10px] font-semibold text-white/85 sm:text-[11px]">{sub}</div>}
      </div>
    </div>
  );
}
